// packages/ui/src/Checkbox.tsx
import React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import clsx from 'clsx';

// Extend the props from the underlying Radix component for full compatibility
type CheckboxProps = React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>;

/**
 * A Neobrutalist-styled checkbox component.
 * It's built on top of Radix UI for accessibility and keyboard navigation.
 */
export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={clsx(
      'peer h-5 w-5 shrink-0 rounded-sm border-2 border-black transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-500',
      'data-[state=checked]:bg-emerald-500 data-[state=checked]:text-white',
      '[.theme-liquid-glass_&]:border-white/30 [.theme-liquid-glass_&]:bg-white/5',
      '[.theme-liquid-glass_&]:text-white [.theme-liquid-glass_&]:backdrop-blur-sm',
      '[.theme-liquid-glass_&]:focus-visible:ring-violet-400 [.theme-liquid-glass_&]:focus-visible:ring-offset-slate-900',
      '[.theme-liquid-glass_&]:data-[state=checked]:bg-violet-500/80 [.theme-liquid-glass_&]:data-[state=checked]:text-white',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className={clsx('flex items-center justify-center text-current')}>
      <Check className="h-4 w-4" strokeWidth={3} />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));

Checkbox.displayName = CheckboxPrimitive.Root.displayName;
